import { fromLonLat } from 'ol/proj';
import { transform } from 'ol/proj';
import ConfigManager from './configuration/configmanager';
import { Geometry } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';

export function formatNumber(number: number, locale?: string): string {
  const decimals = ConfigManager.getInstance().Config.measurements.decimals;
  return number.toLocaleString(locale || ConfigManager.getInstance().Config.general.locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

export function formatCoordinates(coords: number[], locale: string): string[] {
  const east = formatNumber(coords[0], locale);
  const nord = formatNumber(coords[1], locale);
  return [east, nord];
}

/**
 * @param coords 2 numbers
 * @param extent 4 numbers min max
 * @returns true if coords is intersects extent
 */
function isWithinExtent(coords: number[], extent: number[]): boolean {
  return coords[0] >= extent[0] && coords[0] <= extent[2] && coords[1] >= extent[1] && coords[1] <= extent[3];
}

/**
 * Will check if given coords can be mapped to a max extent
 * if not, an empty array is returned
 * @param coords [lat, lon], [lon, lat], [east, north] accepted
 * @param extent bbox of 4 numbers, typically maxExtent defined in config
 * @returns coords in provided srid if extent is given, otherwise, returns coords
 */
export function parseCoordinates(coords: number[], extent: number[] | undefined, srid: string): number[] {
  if (!extent) {
    return coords;
  }

  // when coordinates are in current srid, we check they are in extent
  if (isWithinExtent(coords, extent)) {
    return coords;
  }

  // if given [lon, lat]
  let [east, north] = fromLonLat(coords, srid);
  if (isWithinExtent([east, north], extent)) {
    return [east, north];
  }

  // if given [lat, long]
  coords.reverse();
  [east, north] = fromLonLat(coords, srid);
  if (isWithinExtent([east, north], extent)) {
    return [east, north];
  }

  return [];
}

/**
 * Reprojects one coordinate for measurements
 * @param coords coords in map projection
 * @returns coords in measurements projection
 */
export const coordForMeasurement = (coord: Coordinate): Coordinate => {
  const fromSrid = ConfigManager.getInstance().Config.map.srid;
  const toSrid = ConfigManager.getInstance().Config.measurements.srid;
  return transform(coord, fromSrid, toSrid);
};

/**
 * Reprojects a list of coordinates for measurements
 * @param coords list of coords in map projection
 * @returns list of coords in measurements projection
 */
export const coordsForMeasurement = (coords: Coordinate[]): Coordinate[] => {
  return coords.map(coordForMeasurement);
};

/**
 * Reprojects geometry for measurements
 * @param geom geom in map projection
 * @returns geom in measurements projection
 */
export const geomForMeasurement = <T extends Geometry>(geom: T): T => {
  const fromSrid = ConfigManager.getInstance().Config.map.srid;
  const toSrid = ConfigManager.getInstance().Config.measurements.srid;
  return geom.clone().transform(fromSrid, toSrid) as T;
};
